import type { CSSProperties, FC, ReactNode } from 'react'
import type { Experiment, ExperimentKind } from '@/constants/lab'

import { useState } from 'react'
import { FlaskConical } from 'lucide-react'
import { cn } from '@/lib/utils'
import { hand } from '@/lib/fonts'
import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import { FinalCta } from '@/components/kit/final-cta'
import { InView } from '@/components/kit/in-view'
import { NewsletterForm } from '@/components/kit/newsletter-form'
import { Reveal } from '@/components/kit/reveal'
import { SectionLabel } from '@/components/kit/section-label'
import { SplitText } from '@/components/kit/split-text'

import {
    experimentKinds,
    experiments,
    labMethod,
    runningNow
} from '@/constants/lab'

const verdictTone: Record<Experiment['verdict'], string> = {
    'Confirmed': 'text-[#1f7a43]',
    'Busted': 'text-[#c2330a]',
    'Too close to call': 'text-stone'
}

const tally = {
    run: 41,
    confirmed: experiments.filter(item => item.verdict === 'Confirmed').length + 20,
    busted: experiments.filter(item => item.verdict === 'Busted').length + 8,
    close: experiments.filter(item => item.verdict === 'Too close to call').length + 4
}

const ResultBars: FC<{ experiment: Experiment }> = ({ experiment }) => {
    const max = Math.max(experiment.a.value, experiment.b.value)
    const rows = [experiment.a, experiment.b]
    const winner = experiment.a.value === experiment.b.value ? -1 : experiment.a.value > experiment.b.value ? 0 : 1

    return (
        <InView
            as='figure'
            className='mt-6'
        >
            <figcaption className='font-mono text-[10px] uppercase tracking-[0.14em] text-stone'>{experiment.metric}</figcaption>
            <div className='mt-3 flex flex-col gap-2.5'>
                {rows.map((row, index) => (
                    <div
                        key={row.label}
                        className='grid grid-cols-[6.5rem_1fr_auto] items-center gap-3'
                    >
                        <span className='truncate text-[13px] text-ink/80'>{row.label}</span>
                        <span className='h-3 overflow-hidden rounded-full bg-ink/[0.07]'>
                            <span
                                className={cn('bar-grow-x block h-full rounded-full', index === winner ? 'bg-ember' : 'bg-ink/45')}
                                style={{ width: `${(row.value / max) * 100}%`, '--delay': `${200 + index * 160}ms` } as CSSProperties}
                            />
                        </span>
                        <span className='font-mono text-xs tabular-nums text-ink'>{row.value}</span>
                    </div>
                ))}
            </div>
        </InView>
    )
}

const ExperimentCard: FC<{ experiment: Experiment, index: number }> = ({ experiment, index }) => {
    return (
        <Reveal
            delay={(index % 3) * 90}
            settle={[-0.8, 0.6, -0.4][index % 3]}
            className='h-full'
        >
            <article className='relative flex h-full flex-col overflow-hidden rounded-[1.5rem] border border-ink/10 bg-paper bg-[linear-gradient(to_right,rgb(0_153_255/0.07)_1px,transparent_1px),linear-gradient(to_bottom,rgb(0_153_255/0.07)_1px,transparent_1px)] [background-size:22px_22px] p-6 md:p-7'>
                <div className='flex items-center justify-between gap-3'>
                    <span className='font-mono text-[11px] uppercase tracking-[0.16em] text-stone'>Exp. {experiment.id}</span>
                    <span className='rounded-full border border-ink/15 bg-cream px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.12em]'>{experiment.kind}</span>
                </div>

                <h3 className='mt-5 text-[1.75rem] font-bold leading-[1.02] tracking-[-0.035em]'>{experiment.title}</h3>

                <p className='mt-4 font-mono text-[10px] uppercase tracking-[0.14em] text-stone'>Hypothesis</p>
                <p className='mt-1 text-[15px] leading-relaxed text-ink/85'>{experiment.hypothesis}</p>

                <ResultBars experiment={experiment} />

                <div className='mt-6 border-t border-dashed border-ink/20 pt-4'>
                    <p className='font-mono text-[10px] uppercase tracking-[0.14em] text-stone'>What we learned</p>
                    <p className={cn(hand.className, 'mt-1 text-[1.6rem] leading-[1.05] text-ink')}>{experiment.learned}</p>
                </div>

                <div className='mt-auto flex items-end justify-between gap-3 pt-6'>
                    <span className='font-mono text-[10px] text-stone'>{experiment.sample}</span>
                    <span className={cn('stamp shrink-0 -rotate-6', verdictTone[experiment.verdict])}>{experiment.verdict}</span>
                </div>
            </article>
        </Reveal>
    )
}

const Lab: FC = (): ReactNode => {
    const [kind, setKind] = useState<'All' | ExperimentKind>('All')
    const visible = experiments.filter(item => kind === 'All' || item.kind === kind)

    return (
        <Layout>
            <Seo
                title='The Lab'
                description='Small public experiments from Updraft: hook tests, thumbnail tests, caption tests and timing tests, each with a hypothesis, a result and what we learned.'
            />

            <section
                aria-labelledby='lab-title'
                className='relative overflow-hidden pb-16 pt-36 md:pb-24 md:pt-44'
            >
                <div
                    aria-hidden='true'
                    className='absolute inset-0 bg-[linear-gradient(to_right,rgb(0_153_255/0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgb(0_153_255/0.08)_1px,transparent_1px)] [background-size:28px_28px] [mask-image:linear-gradient(to_bottom,#000_40%,transparent)]'
                />

                <div className='relative mx-auto max-w-[1400px] px-5 md:px-10'>
                    <SectionLabel>The Lab · open notebook</SectionLabel>
                    <SplitText
                        as='h1'
                        id='lab-title'
                        lines={['Small tests,', 'honest results']}
                        italic='honest'
                        className='mt-6 text-[clamp(3.25rem,10vw,9.25rem)] font-bold leading-[0.86] tracking-[-0.045em]'
                    />

                    <div className='mt-12 grid gap-10 lg:grid-cols-12 lg:items-end'>
                        <Reveal
                            delay={250}
                            className='lg:col-span-5'
                        >
                            <p className='text-xl leading-relaxed text-stone'>
                                We run little experiments on hooks, thumbnails, captions and timing, on real client posts, then publish what happened. Including the ones where we were wrong.
                            </p>
                            <p className={cn(hand.className, 'mt-6 -rotate-2 text-2xl text-ember')}>results may vary. ours did.</p>
                        </Reveal>

                        <Reveal
                            delay={350}
                            className='lg:col-span-6 lg:col-start-7'
                        >
                            <dl className='grid grid-cols-2 gap-3 sm:grid-cols-4'>
                                {[
                                    { label: 'Tests run', value: tally.run },
                                    { label: 'Confirmed', value: tally.confirmed },
                                    { label: 'Busted', value: tally.busted },
                                    { label: 'Too close', value: tally.close }
                                ].map(item => (
                                    <div
                                        key={item.label}
                                        className='rounded-2xl border border-ink/10 bg-paper p-4'
                                    >
                                        <dt className='font-mono text-[10px] uppercase tracking-[0.14em] text-stone'>{item.label}</dt>
                                        <dd className='mt-2 font-heading text-4xl font-bold leading-none tracking-[-0.04em]'>{item.value}</dd>
                                    </div>
                                ))}
                            </dl>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section
                aria-labelledby='experiments-title'
                className='mx-auto max-w-[1400px] px-5 pb-20 md:px-10 md:pb-28'
            >
                <div className='flex flex-col gap-6 border-t border-ink/15 pt-8 md:flex-row md:items-center md:justify-between'>
                    <h2
                        id='experiments-title'
                        className='text-[clamp(2rem,3.6vw,3rem)] font-bold tracking-[-0.04em]'
                    >
                        The <span className='font-serif font-normal italic tracking-[-0.01em]'>results</span> so far
                    </h2>
                    <div
                        role='group'
                        aria-label='Filter experiments'
                        className='flex flex-wrap gap-2'
                    >
                        {(['All', ...experimentKinds] as const).map(item => (
                            <button
                                key={item}
                                type='button'
                                aria-pressed={kind === item}
                                onClick={() => setKind(item)}
                                className={cn(
                                    'rounded-full border px-4 py-2 text-sm font-medium transition-colors duration-300',
                                    kind === item ? 'border-ink bg-ink text-cream' : 'border-ink/15 bg-paper/60 text-ink hover:border-ink/40'
                                )}
                            >
                                {item}
                            </button>
                        ))}
                    </div>
                </div>

                <p
                    className='sr-only'
                    aria-live='polite'
                >
                    {visible.length === 1 ? 'Showing 1 experiment' : `Showing ${visible.length} experiments`}
                </p>

                <div className='mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3'>
                    {visible.map((experiment, index) => (
                        <ExperimentCard
                            key={experiment.id}
                            experiment={experiment}
                            index={index}
                        />
                    ))}
                </div>
            </section>

            <section
                aria-labelledby='method-title'
                className='bg-ink py-20 text-cream md:py-28'
            >
                <div className='mx-auto max-w-[1400px] px-5 md:px-10'>
                    <SectionLabel tone='cream'>Method</SectionLabel>
                    <SplitText
                        as='h2'
                        id='method-title'
                        lines={['How a test', 'gets run']}
                        italic='test'
                        className='mt-5 text-[clamp(2.5rem,5.6vw,5.25rem)] font-bold leading-[0.9] tracking-[-0.045em]'
                    />

                    <ol className='mt-14 grid gap-px overflow-hidden rounded-[1.75rem] bg-cream/10 md:grid-cols-2 lg:grid-cols-4'>
                        {labMethod.map((item, index) => (
                            <Reveal
                                as='li'
                                key={item.step}
                                delay={index * 90}
                                className='bg-ink p-7'
                            >
                                <p className='font-mono text-xs text-ember'>Step {item.step}</p>
                                <h3 className='mt-4 text-2xl font-bold tracking-[-0.035em]'>{item.title}</h3>
                                <p className='mt-3 text-[15px] leading-relaxed text-cream/70'>{item.body}</p>
                            </Reveal>
                        ))}
                    </ol>
                </div>
            </section>

            <section
                aria-labelledby='running-title'
                className='mx-auto max-w-[1400px] px-5 py-20 md:px-10 md:py-28'
            >
                <div className='grid gap-12 lg:grid-cols-12'>
                    <div className='lg:col-span-6'>
                        <SectionLabel>On the bench</SectionLabel>
                        <SplitText
                            as='h2'
                            id='running-title'
                            lines={['Running right', 'now']}
                            italic='now'
                            className='mt-5 text-[clamp(2.5rem,5.6vw,5.25rem)] font-bold leading-[0.9] tracking-[-0.045em]'
                        />

                        <ul className='mt-10 flex flex-col gap-4'>
                            {runningNow.map((item, index) => (
                                <Reveal
                                    as='li'
                                    key={item.id}
                                    delay={index * 90}
                                >
                                    <div className='rounded-2xl border border-ink/10 bg-paper p-5'>
                                        <div className='flex items-center justify-between gap-4'>
                                            <p className='flex items-center gap-3 font-semibold'>
                                                <FlaskConical className='size-4 text-ember' />
                                                {item.title}
                                            </p>
                                            <span className='shrink-0 font-mono text-[11px] text-stone'>{item.daysLeft} days left</span>
                                        </div>
                                        <InView className='mt-4 h-2 overflow-hidden rounded-full bg-ink/[0.07]'>
                                            <span
                                                className='bar-grow-x block h-full rounded-full bg-ember'
                                                style={{ width: `${item.progress * 100}%` }}
                                            />
                                        </InView>
                                        <p className='mt-2 font-mono text-[10px] uppercase tracking-[0.14em] text-stone'>Exp. {item.id} · {Math.round(item.progress * 100)}% through</p>
                                    </div>
                                </Reveal>
                            ))}
                        </ul>
                    </div>

                    <Reveal
                        delay={150}
                        settle={1.2}
                        className='lg:col-span-5 lg:col-start-8'
                    >
                        <div className='relative rounded-[2rem] bg-butter p-8 md:p-10'>
                            <span
                                aria-hidden='true'
                                className='tape -top-3 right-10 rotate-6'
                            />
                            <p className='font-mono text-[11px] uppercase tracking-[0.16em] text-ink/70'>Lab notes, by email</p>
                            <p className='mt-4 font-heading text-[clamp(2rem,3.4vw,2.75rem)] font-bold leading-[1] tracking-[-0.04em]'>
                                Get the result the day a test closes.
                            </p>
                            <p className='mt-3 text-base leading-relaxed text-ink/75'>
                                Lab results go out first in the Tuesday Signal, before they land on this page.
                            </p>
                            <NewsletterForm
                                source='lab'
                                className='mt-7'
                            />
                        </div>
                    </Reveal>
                </div>
            </section>

            <FinalCta
                lines={['Want us to test', 'your hunch?']}
                italic='your'
                body='Bring us the idea your team keeps arguing about. We will design a fair test, run it on your posts and tell you who was right.'
            />
        </Layout>
    )
}

export default Lab
